import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import {
  approveJobAdApplicants,
  ApproveJobAdApplicantsParameters,
  AssignmentCoverLetter,
  AssignmentCoverLetterParameters,
  getJobAdApplicantCoverLetter,
  getJobAdApplicants,
  JobAdApplicantRejectionMessageSent,
  JobAdApplicant,
  JobAdApplicantsApproveChanged,
  JobApplicantsParameters,
  sendJobAdApplicantRejectedMessage,
  SendJobAdApplicantRejectedMessageParameters,
} from "./jobAdApplicantsAPI";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../../app/createApiAsyncThunk";

// Slice state typing
export type SliceState = {
  ids: string[];
  entities: {
    [id: string]: JobAdApplicant;
  };
  status: "idle" | "pending" | "succeeded" | "failed";
  cursor: string | null;
  error: string | null;
};

const jobAdApplicantsAdapter = createEntityAdapter<JobAdApplicant>();

export const fetchJobAdApplicants = createAsyncThunk<
  any,
  JobApplicantsParameters,
  { state: RootState }
>("jobAdApplicants/fetchJobAdApplicants", async (params, { requestId }) => {
  const res = await getJobAdApplicants(params, { requestId });
  return res;
});

export const postApproveJobAdApplicants = createCommandAsyncThunk<
  ApproveJobAdApplicantsParameters,
  JobAdApplicantsApproveChanged
>("jobAdApplicants/approveJobAdApplicants", approveJobAdApplicants);

export const postSendJobAdApplicantRejectedMessage = createAsyncThunk<
  any,
  SendJobAdApplicantRejectedMessageParameters,
  { state: RootState }
>(
  "jobAdApplicants/sendJobAdApplicantRejectedMessage",
  async (data, { requestId }) => {
    const res = await sendJobAdApplicantRejectedMessage(data, { requestId });
    return res;
  },
);

export const fetchApplicantCoverLetter = createQueryAsyncThunk<
  AssignmentCoverLetter,
  AssignmentCoverLetterParameters
>("jobAdApplicants/fetchApplicantCoverLetter", getJobAdApplicantCoverLetter);

const jobAdApplicantsSlice = createSlice({
  name: "jobAdApplicants",
  initialState: jobAdApplicantsAdapter.getInitialState({
    status: "idle",
    cursor: null,
  }) as SliceState,
  reducers: {
    // Clear all applicants from the state
    clearApplicants: (state) => {
      jobAdApplicantsAdapter.removeAll(state);
      state.status = "idle";
      state.cursor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobAdApplicants.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchJobAdApplicants.fulfilled, (state, action) => {
        if (action.payload?.rows) {
          // Use the rows to update the adapter
          jobAdApplicantsAdapter.setAll(state, action.payload.rows);
        } else {
          // Handle cases where rows are missing by setting an empty array
          jobAdApplicantsAdapter.setAll(state, []);
        }

        // Set the cursor from action.payload.cursor, if available
        state.cursor = action.payload?.cursor || null;

        state.status = "succeeded";
      })
      .addCase(fetchJobAdApplicants.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(postApproveJobAdApplicants.pending, (state) => {
        state.status = "pending";
      })
      .addCase(postApproveJobAdApplicants.fulfilled, (state, action) => {
        // Directly find the jbAdAppApprvChg event
        const approveChangedEvent = action.payload.find(
          (event: any) => event.name === "jbAdAppApprvChg",
        );

        // If the event exists and has a payload, proceed
        if (approveChangedEvent?.payload) {
          const {
            applicantsIds,
            eezyAccepted,
            rejectionReason,
            rejectionComment,
          } = approveChangedEvent.payload as JobAdApplicantsApproveChanged;

          // Map applicants to the required update format
          const updates = applicantsIds.map((id) => ({
            id,
            changes: {
              eezyAccepted,
              rejectionReason: rejectionReason ?? null,
              rejectionComment: rejectionComment ?? null,
            },
          }));

          // Apply updates to the applicants in the state
          jobAdApplicantsAdapter.updateMany(state, updates);

          // Set status to succeeded
          state.status = "succeeded";
        } else {
          state.status = "succeeded";
        }
      })
      .addCase(postApproveJobAdApplicants.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(postSendJobAdApplicantRejectedMessage.pending, (state) => {
        state.status = "pending";
      })
      .addCase(
        postSendJobAdApplicantRejectedMessage.fulfilled,
        (state, action) => {
          const sentEvents = action.payload.filter(
            (event: any) => event.name === "jbAdAppRjctnMsg",
          );

          if (sentEvents.length > 0) {
            const now = Date.now();
            const updates = sentEvents.map((event: any) => ({
              id: (event.payload as JobAdApplicantRejectionMessageSent)
                .applicantId,
              changes: { rejectionMessageSentAt: now },
            }));

            jobAdApplicantsAdapter.updateMany(state, updates);
          }
          state.status = "succeeded";
        },
      )
      .addCase(postSendJobAdApplicantRejectedMessage.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchApplicantCoverLetter.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchApplicantCoverLetter.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(fetchApplicantCoverLetter.rejected, (state) => {
        state.status = "failed";
      });
  },
});

// Selectors
export const {
  selectAll: selectAllJobAdApplicants,
  selectById: selectJobAdApplicantsById,
} = jobAdApplicantsAdapter.getSelectors(
  (state: RootState) => state.jobAdApplicants,
);

export const { clearApplicants } = jobAdApplicantsSlice.actions;

export default jobAdApplicantsSlice.reducer;
